class BDProductMedia extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    // ⚠️ CRITICAL FIX: Store context so fetchUpdatedMediaSection can use it
    this.context = this.closest("[data-product-grid-content]") || this.closest("quick-add-dialog");
    if (!this.context) return;

    this.variantPicker = this.context.querySelector("variant-picker");
    if (!this.variantPicker) return;

    this._onVariantUpdate = this.handleVariantUpdate.bind(this);
    
    // Listen on context so we catch the event even if the picker DOM changes slightly
    this.context.addEventListener("variant:update", this._onVariantUpdate);

    // Event delegation for lightbox clicks
    this.addEventListener("click", (e) => {
      const zoomEl = e.target.closest("[data-zoom]");
      if (zoomEl) this.#openLightboxFromZoom(zoomEl);
    });
  }

  disconnectedCallback() {
    if (this.context) {
      this.context.removeEventListener("variant:update", this._onVariantUpdate);
    }
    // Clean up any pending fetches if the element is removed from the DOM
    this.#abortController?.abort();
  }

  handleVariantUpdate() {
    const checked = this.variantPicker.querySelector("input[data-variant-id]:checked");
    const variantId = checked?.dataset.variantId;
    if (!variantId) return;

    this.fetchUpdatedMediaSection(variantId);
  }

  async fetchUpdatedMediaSection(variantId) {
    // ⭐ OPTIMIZATION: Abort the previous fetch if the user clicks rapidly.
    // This prevents race conditions where an older, slower fetch finishes 
    // after a newer one and overwrites the correct image.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      // 1. Determine Base URL (Modal vs Standard Page)
      const quickAddComp = document.querySelector('quick-add-component[stay-visible]');
      const baseUrl = quickAddComp?.productPageUrl || window.location.pathname;

      // 2. Determine Section ID
      const sectionInput = this.context.querySelector('input[name="section_id"]');
      const sectionId = sectionInput?.value || this.closest("[data-section-id]")?.dataset.sectionId;

      // 3. Build URL
      const tempUrl = new URL(baseUrl, window.location.origin);
      tempUrl.searchParams.set('variant', variantId);
      if (sectionId) {
        tempUrl.searchParams.set('section_id', sectionId);
      }

      // 4. Fetch with AbortController signal
      const response = await fetch(tempUrl.toString(), { signal });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const updated = doc.querySelector("bd-product-media");
      
      if (updated) {
        this.updateFromHtml(updated);
      }
    } catch (error) {
      // Don't log errors if we intentionally aborted the request (fast clicking)
      if (error.name !== 'AbortError') {
        console.error("BDProductMedia: Failed to fetch media.", error);
      }
    }
  }

  updateFromHtml(updated) {
    this.classList.add("is-updating");

    // Timeout allows the CSS fade-out transition to start
    setTimeout(() => {
      this.querySelector("bd-slider")?.destroySlider?.();
      this.innerHTML = updated.innerHTML;

      const slider = this.querySelector("bd-slider");
      slider?.initSlider?.();
      slider?.embla?.scrollTo(0, false);

      if (typeof bindMediaLoaders === "function") {
        bindMediaLoaders(this);
      }

      // Trigger fade-in
      requestAnimationFrame(() => {
        this.classList.remove("is-updating");
      });
    }, 150); // Adjust this to match your CSS transition duration
  }

  #openLightboxFromZoom(zoomEl) {
    const slider = this.querySelector("bd-slider");
    const lightbox = document.querySelector("bd-lightbox");
    
    // ⚠️ SAFETY: Ensure slider and lightbox exist before trying to use them
    if (!slider?.embla || !lightbox) return;

    const slides = slider.embla.slideNodes();
    const index = slides.findIndex((s) => s.contains(zoomEl));
    
    // If the clicked element isn't inside a known slide, do nothing
    if (index === -1) return;

    // .filter(Boolean) ensures we don't pass undefined broken image URLs to the lightbox
    const images = slides.map((s) => {
      const img = s.querySelector("[data-zoom] img");
      return img?.dataset.zoomSrc || img?.src;
    }).filter(Boolean);

    lightbox.open(images, index);
  }
}

customElements.define("bd-product-media", BDProductMedia);